
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";

// Sample data - In a real app, this would come from an API or state
const weeklyData = [
  { day: "Mon", nutrition: 85, supplements: 90, fitness: 70, mindfulness: 60 },
  { day: "Tue", nutrition: 75, supplements: 90, fitness: 80, mindfulness: 75 },
  { day: "Wed", nutrition: 90, supplements: 85, fitness: 65, mindfulness: 85 },
  { day: "Thu", nutrition: 80, supplements: 90, fitness: 75, mindfulness: 70 },
  { day: "Fri", nutrition: 85, supplements: 95, fitness: 90, mindfulness: 60 },
  { day: "Sat", nutrition: 95, supplements: 90, fitness: 85, mindfulness: 90 },
  { day: "Sun", nutrition: 90, supplements: 90, fitness: 70, mindfulness: 80 },
];

const monthlyData = [
  { week: "Week 1", nutrition: 80, supplements: 90, fitness: 75, mindfulness: 65 },
  { week: "Week 2", nutrition: 85, supplements: 85, fitness: 80, mindfulness: 75 },
  { week: "Week 3", nutrition: 90, supplements: 90, fitness: 85, mindfulness: 80 },
  { week: "Week 4", nutrition: 95, supplements: 95, fitness: 90, mindfulness: 85 },
];

const wellnessColors = {
  nutrition: "#4CAF50",
  supplements: "#2196F3",
  fitness: "#FF5722",
  mindfulness: "#9C27B0",
};

export function ProgressChart() {
  const [period, setPeriod] = useState("weekly");
  const [chartType, setChartType] = useState("line");

  const data = period === "weekly" ? weeklyData : monthlyData;
  const xAxisKey = period === "weekly" ? "day" : "week";

  return (
    <Card className="glass-card">
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <CardTitle>Wellness Progress</CardTitle>
          <div className="flex space-x-2">
            <Tabs value={period} onValueChange={setPeriod} className="w-[160px]">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="weekly">Weekly</TabsTrigger>
                <TabsTrigger value="monthly">Monthly</TabsTrigger>
              </TabsList>
            </Tabs>
            <Tabs value={chartType} onValueChange={setChartType} className="w-[160px]">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="line">Line</TabsTrigger>
                <TabsTrigger value="bar">Bar</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="h-[300px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            {chartType === "line" ? (
              <LineChart data={data} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.2} />
                <XAxis 
                  dataKey={xAxisKey} 
                  tick={{ fontSize: 12 }}
                  tickLine={{ stroke: 'rgba(107, 114, 128, 0.2)' }}
                  axisLine={{ stroke: 'rgba(107, 114, 128, 0.2)' }}
                />
                <YAxis 
                  tick={{ fontSize: 12 }}
                  tickLine={{ stroke: 'rgba(107, 114, 128, 0.2)' }}
                  axisLine={{ stroke: 'rgba(107, 114, 128, 0.2)' }}
                  domain={[0, 100]}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    borderRadius: '0.5rem',
                    border: '1px solid rgba(229, 231, 235, 1)',
                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
                  }}
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="nutrition"
                  stroke={wellnessColors.nutrition}
                  strokeWidth={2}
                  dot={{ r: 4, strokeWidth: 2 }}
                  activeDot={{ r: 6 }}
                />
                <Line
                  type="monotone"
                  dataKey="supplements"
                  stroke={wellnessColors.supplements}
                  strokeWidth={2}
                  dot={{ r: 4, strokeWidth: 2 }}
                  activeDot={{ r: 6 }}
                />
                <Line
                  type="monotone"
                  dataKey="fitness"
                  stroke={wellnessColors.fitness}
                  strokeWidth={2}
                  dot={{ r: 4, strokeWidth: 2 }}
                  activeDot={{ r: 6 }}
                />
                <Line
                  type="monotone"
                  dataKey="mindfulness"
                  stroke={wellnessColors.mindfulness}
                  strokeWidth={2}
                  dot={{ r: 4, strokeWidth: 2 }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            ) : (
              <BarChart data={data} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.2} />
                <XAxis 
                  dataKey={xAxisKey} 
                  tick={{ fontSize: 12 }}
                  tickLine={{ stroke: 'rgba(107, 114, 128, 0.2)' }}
                  axisLine={{ stroke: 'rgba(107, 114, 128, 0.2)' }}
                />
                <YAxis 
                  tick={{ fontSize: 12 }}
                  tickLine={{ stroke: 'rgba(107, 114, 128, 0.2)' }}
                  axisLine={{ stroke: 'rgba(107, 114, 128, 0.2)' }}
                  domain={[0, 100]}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    borderRadius: '0.5rem',
                    border: '1px solid rgba(229, 231, 235, 1)',
                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
                  }}
                />
                <Legend />
                <Bar dataKey="nutrition" fill={wellnessColors.nutrition} radius={[4, 4, 0, 0]} />
                <Bar dataKey="supplements" fill={wellnessColors.supplements} radius={[4, 4, 0, 0]} />
                <Bar dataKey="fitness" fill={wellnessColors.fitness} radius={[4, 4, 0, 0]} />
                <Bar dataKey="mindfulness" fill={wellnessColors.mindfulness} radius={[4, 4, 0, 0]} />
              </BarChart>
            )}
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}
